/* eslint-disable import/prefer-default-export */
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import t from 'react-translate';
import { Result } from 'redux/schemas/api';
import { FetchCourseTimelineParams, fetchCourseTimelineParamsDefault, CourseTimelineResponse } from 'redux/schemas/api/timelines';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { LecturePage } from 'redux/schemas/models/lecture-page';
import { LectureComponent } from '../schemas/models/lecture-component';
import { addAlertMessage } from './alert-messages';

async function fetchCourseTimeline(params: FetchCourseTimelineParams = fetchCourseTimelineParamsDefault) {
  const response = await axios.get<Result<CourseTimelineResponse>>(`${params.catalogId}/users/${params.userId}/course_timeline.json?lite=${params.outlineOnly}`);
  return response.data.result;
}

export const getCourseTimeline = createAsyncThunk(
  'GET_COURSE_TIMELINE',
  fetchCourseTimeline,
);


export type FetchLecturePageActivitiesParams = {
  catalogId: string,
  lecturePageId: number,
};

/**
 * gets all activities(in lecture component form) for the given lecture page
 * activity = any lecture component that a user can interact with(not display-only like RTE, resources, templates)
 * @param params
 * @returns LectureComponent[]
 */
async function fetchLecturePageActivities(params: FetchLecturePageActivitiesParams) {
  const response = await axios.get<Result<LectureComponent[]>>(`${params.catalogId}/lecture_pages/${params.lecturePageId}/activities.json`);
  return response.data.result;
}

export const getLecturePageActivities = createAsyncThunk(
  'LOAD_LECTURE_PAGE_ACTIVITIES',
  fetchLecturePageActivities,
);

/** This has a generic name but is specifically used to get a new version of the current lecture page after a lecture component is modified. This returns
 * many lecture page fields but I think only the status files like isComplete are relevant
 * Note that we don't handle this via a reducer; instead we pass the response into
 * a getLecturePage action */
export const getCourseLecturePageTimeline = createAsyncThunk(
  'LOAD_LECTURE_PAGE_COURSE_OUTLINE',
  async (params: {
    catalogId: string,
    userId: number,
    lecturePageId: number,
  }, thunkAPI) => {
    const response = await axios.get<Result<LecturePage>>(`${params.catalogId}/users/${params.userId}/course_timeline_lecture_page.json?lecture_page_id=${params.lecturePageId}`);

    // // Manually fire a synchronous getLecturePage fulfilled action to load this response
    // // data into Redux in the same manner as when we normally request a lecture page
    // thunkAPI.dispatch(updateLecturePage.fulfilled(response.data.result, thunkAPI.requestId, {
    //   /* This action only uses this lecturepage param to send lp data up to the backend, and not in the reducer */
    //   lecturePage: null,
    //   catalogId: params.catalogId,
    //   lecturePageId: params.lecturePageId.toString(),
    // }));

    return response.data.result;
  },
);

export const deleteLectureSection = createAsyncThunk(
  'DELETE_LECTURE_SECTION',
  async (params: {
    catalogId: string,
    lectureSectionId: number,
    keepContent: boolean,
  }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.delete(`/${params.catalogId}/lecture_sections/${params.lectureSectionId}?keep_content=${params.keepContent}`);
      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return rejectWithValue(error);
    }
  },
);
